<template>
  <Modal title="CRM Update" @close="close">
    <div class="detail">
      <div class="detailRow">
        <div class="detailLabel">Account</div>
        <div class="detailValue">{{ accountName }}</div>
      </div>
      <div class="detailRow">
        <div class="detailLabel">Provider</div>
        <div class="detailValue">{{ modification?.provider_label || modification?.provider }}</div>
      </div>
      <div class="detailRow">
        <div class="detailLabel">Trigger</div>
        <div class="detailValue" :title="modification?.kind">{{ modification?.trigger_label || modification?.kind_label || modification?.kind }}</div>
      </div>
      <div
        class="detailRow"
        v-if="modification?.salesforce_account_id || modification?.salesforce_contact_id || modification?.salesforce_task_id"
      >
        <div class="detailLabel">Objects</div>
        <div class="detailValue">
          <div class="objects">
            <a
              v-if="modification?.salesforce_account_url"
              class="objPill"
              :class="{ created: modification?.created_account }"
              :href="modification.salesforce_account_url"
              target="_blank"
              rel="noopener"
              :title="modification?.created_account ? 'Account created' : 'Account matched'"
            >
              Account
            </a>
            <span
              v-else-if="modification?.salesforce_account_id"
              class="objPill"
              :class="{ created: modification?.created_account }"
              :title="modification?.created_account ? 'Account created' : 'Account matched'"
            >
              Account
            </span>

            <a
              v-if="modification?.salesforce_contact_url"
              class="objPill"
              :class="{ created: modification?.created_contact }"
              :href="modification.salesforce_contact_url"
              target="_blank"
              rel="noopener"
              :title="modification?.created_contact ? 'Contact created' : 'Contact matched'"
            >
              Contact
            </a>
            <span
              v-else-if="modification?.salesforce_contact_id"
              class="objPill"
              :class="{ created: modification?.created_contact }"
              :title="modification?.created_contact ? 'Contact created' : 'Contact matched'"
            >
              Contact
            </span>

            <a
              v-if="modification?.salesforce_task_url"
              class="objPill created"
              :href="modification.salesforce_task_url"
              target="_blank"
              rel="noopener"
              title="Task created"
            >
              Task
            </a>
            <span v-else-if="modification?.salesforce_task_id" class="objPill created" title="Task created">Task</span>
          </div>
        </div>
      </div>
      <div class="detailRow">
        <div class="detailLabel">Status</div>
        <div class="detailValue">
          <span class="pill" :class="`pill-${modification?.status}`">{{ modification?.status }}</span>
          <span v-if="modification?.requires_approval" class="pill pill-warn" style="margin-left:6px;">requires approval</span>
        </div>
      </div>
      <div class="detailRow" v-if="modification?.approval_reason">
        <div class="detailLabel">Approval reason</div>
        <div class="detailValue">{{ modification?.approval_reason }}</div>
      </div>
      <div class="detailRow" v-if="modification?.last_error">
        <div class="detailLabel">Last error</div>
        <div class="detailValue">{{ modification?.last_error }}</div>
      </div>
      <div class="detailRow">
        <div class="detailLabel">Attempts</div>
        <div class="detailValue">{{ Number(modification?.attempt_count || 0) }}</div>
      </div>
      <div class="detailRow" v-if="modification?.reachout_id">
        <div class="detailLabel">Reachout</div>
        <div class="detailValue">{{ modification?.reachout_id }}</div>
      </div>

      <div class="detailRow" v-if="modification?.created_by_agent_run_id">
        <div class="detailLabel">Agent run</div>
        <div class="detailValue">
          <a href="#" @click.prevent="openRun">{{ modification?.created_by_agent_run_id }}</a>
        </div>
      </div>

      <div v-if="String(modification?.status || '') === 'pending_approval'" class="detailBlock">
        <div class="detailLabel">Approve account creation</div>
        <div class="hint" style="margin-top:0;">You can edit the Salesforce Account name before approving.</div>
        <div class="field" style="margin-top:10px;">
          <label>Account name</label>
          <input class="input" type="text" v-model="approveAccountName" placeholder="Account name" />
        </div>
        <div class="field" style="margin-top:10px;">
          <label>Reject reason (optional)</label>
          <input class="input" type="text" v-model="rejectReason" placeholder="Reason" />
        </div>
      </div>

      <div class="detailBlock" v-if="modification?.payload && Object.keys(modification.payload).length">
        <div class="detailLabel">Payload</div>
        <pre class="payload">{{ JSON.stringify(modification.payload, null, 2) }}</pre>
      </div>

      <div class="detailBlock" v-if="modification?.result && Object.keys(modification.result).length">
        <div class="detailLabel">Result</div>
        <pre class="payload">{{ JSON.stringify(modification.result, null, 2) }}</pre>
      </div>

      <div v-if="modalError" class="error" style="margin-top:10px;">{{ modalError }}</div>
    </div>

    <template #footer>
      <button class="btn" @click="close">Close</button>
      <button class="btn" :disabled="actionLoading" v-if="canRetry(modification)" @click="retry">{{ actionLoading ? 'Working…' : 'Retry' }}</button>
      <button class="btn" :disabled="actionLoading" v-if="String(modification?.status||'') === 'pending_approval'" @click="reject">{{ actionLoading ? 'Working…' : 'Reject' }}</button>
      <button class="btn primary" :disabled="actionLoading" v-if="String(modification?.status||'') === 'pending_approval'" @click="approve">{{ actionLoading ? 'Working…' : 'Approve' }}</button>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Modal from './Modal.vue'
import { apiFetch } from '../services/api.ts'

const props = defineProps({
  modification: { type: Object, required: true },
})

const emit = defineEmits(['close', 'updated'])

const router = useRouter()

const approveAccountName = ref('')
const rejectReason = ref('')
const modalError = ref('')
const actionLoading = ref(false)

const accountName = computed(() => {
  return String(props.modification?.account_name || props.modification?.account_domain || '').trim()
})

watch(
  () => props.modification?.id,
  () => {
    modalError.value = ''
    rejectReason.value = ''
    approveAccountName.value = String(props.modification?.approved_account_name || props.modification?.payload?.account_name || '').trim()
  },
  { immediate: true },
)

function close() {
  emit('close')
}

function openRun() {
  const id = props.modification?.created_by_agent_run_id
  if (!id) return
  router.push(`/agent-runs/${encodeURIComponent(id)}`)
}

function canRetry(m) {
  const s = String(m?.status || '')
  return s === 'failed' || s === 'queued' || s === 'processing' || s === 'skipped'
}

async function refreshSelf() {
  const id = props.modification?.id
  if (!id) return
  try {
    const updated = await apiFetch(`/crm_modifications/${encodeURIComponent(id)}`)
    emit('updated', updated)
  } catch {
  }
}

async function approve() {
  if (!props.modification?.id) return
  actionLoading.value = true
  modalError.value = ''
  try {
    await apiFetch(`/crm_modifications/${encodeURIComponent(props.modification.id)}/approve`, {
      method: 'POST',
      body: { account_name: String(approveAccountName.value || '').trim() || null },
    })
    await refreshSelf()
  } catch (e) {
    modalError.value = e?.message || 'Failed'
  } finally {
    actionLoading.value = false
  }
}

async function reject() {
  if (!props.modification?.id) return
  actionLoading.value = true
  modalError.value = ''
  try {
    await apiFetch(`/crm_modifications/${encodeURIComponent(props.modification.id)}/reject`, {
      method: 'POST',
      body: { reason: String(rejectReason.value || '').trim() || null },
    })
    await refreshSelf()
  } catch (e) {
    modalError.value = e?.message || 'Failed'
  } finally {
    actionLoading.value = false
  }
}

async function retry() {
  if (!props.modification?.id) return
  actionLoading.value = true
  modalError.value = ''
  try {
    await apiFetch(`/crm_modifications/${encodeURIComponent(props.modification.id)}/retry`, { method: 'POST' })
    await refreshSelf()
  } catch (e) {
    modalError.value = e?.message || 'Failed'
  } finally {
    actionLoading.value = false
  }
}
</script>

<style scoped>
.error { color: var(--danger); }

.detail {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detailRow {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 10px;
}

.detailLabel {
  color: var(--muted);
  font-size: 12px;
}

.detailValue {
  font-size: 13px;
  color: var(--text);
}

.objects {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
}

.objPill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border);
  background: var(--badge-bg);
  color: var(--text);
  text-decoration: none;
}

.objPill.created {
  border-color: var(--status-success-border);
  background: var(--status-success-bg);
  color: var(--status-success-text);
}

.detailBlock {
  border-top: 1px solid var(--border);
  padding-top: 10px;
  margin-top: 6px;
}

.payload {
  margin: 0;
  padding: 10px;
  background: var(--md-pre-bg);
  color: var(--md-pre-text);
  border-radius: 10px;
  font-size: 12px;
  overflow: auto;
  max-height: 360px;
}

.hint {
  color: var(--muted);
  font-size: 12px;
}

.field label {
  display: block;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 6px;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border);
  color: var(--text);
  background: var(--badge-bg);
}

.pill-warn {
  border-color: var(--status-warning-border);
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
}

.pill-pending_approval {
  border-color: var(--status-warning-border);
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
}

.pill-failed {
  border-color: var(--status-danger-border);
  background: var(--status-danger-bg);
  color: var(--status-danger-text);
}

.pill-applied {
  border-color: var(--status-success-border);
  background: var(--status-success-bg);
  color: var(--status-success-text);
}

.pill-rejected {
  border-color: var(--status-neutral-border);
  background: var(--status-neutral-bg);
  color: var(--status-neutral-text);
}
</style>
